/**
 * Question Set Admin API client
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7, 7.8 - Question set management
 */
import { createAuthenticatedClient } from '../../../shared/api/axiosClient';
import type {
  QuestionSet,
  QuestionSetListItem,
  CreateQuestionSetDTO,
  UpdateQuestionSetDTO,
} from '../types/questionSet.types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

let apiClient: ReturnType<typeof createAuthenticatedClient> | null = null;

const getApiClient = () => {
  if (!apiClient) {
    apiClient = createAuthenticatedClient(API_BASE_URL, () => {
      window.location.href = '/login';
    });
  }
  return apiClient;
};

export const questionSetApi = {
  /**
   * Get all question sets (admin view)
   */
  async getAll(): Promise<QuestionSetListItem[]> {
    const client = getApiClient();
    const response = await client.get<QuestionSetListItem[]>('/admin/question-sets');
    return response.data;
  },

  /**
   * Get question set by ID
   */
  async getById(id: string): Promise<QuestionSet> {
    const client = getApiClient();
    const response = await client.get<QuestionSet>(`/admin/question-sets/${id}`);
    return response.data;
  },

  /**
   * Create new question set
   */
  async create(data: CreateQuestionSetDTO): Promise<QuestionSet> {
    const client = getApiClient();
    const response = await client.post<QuestionSet>('/admin/question-sets', data);
    return response.data;
  },

  /**
   * Update existing question set
   */
  async update(id: string, data: UpdateQuestionSetDTO): Promise<QuestionSet> {
    const client = getApiClient();
    const response = await client.put<QuestionSet>(`/admin/question-sets/${id}`, data);
    return response.data;
  },

  /**
   * Delete question set
   */
  async delete(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`/admin/question-sets/${id}`);
  },

  /**
   * Publish question set
   */
  async publish(id: string): Promise<QuestionSet> {
    const client = getApiClient();
    const response = await client.post<QuestionSet>(`/admin/question-sets/${id}/publish`);
    return response.data;
  },

  /**
   * Unpublish question set
   */
  async unpublish(id: string): Promise<QuestionSet> {
    const client = getApiClient();
    const response = await client.post<QuestionSet>(`/admin/question-sets/${id}/unpublish`);
    return response.data;
  },

  /**
   * Reorder questions in a question set
   */
  async reorderQuestions(id: string, questionIds: string[]): Promise<void> {
    const client = getApiClient();
    await client.put(`/admin/question-sets/${id}/reorder`, { questionIds });
  },
};
